import { CommonModule, DatePipe } from '@angular/common';
import { Component, DestroyRef, inject, signal } from '@angular/core';
import { RouterLink } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { I18nService } from '../core/i18n.service';
import { TournamentsApiService } from '../core/tournaments-api.service';
import { TournamentPublic } from '../core/tournaments-api.types';

@Component({
  selector: 'app-tournaments-list-page',
  standalone: true,
  imports: [CommonModule, RouterLink, DatePipe],
  template: `
    <section class="page">
      <header class="page-header card">
        <h1>{{ t('public.list.title') }}</h1>
        <p>{{ t('public.list.subtitle') }}</p>
        @if (!loading() && !error()) {
          <p class="kpi">{{ t('public.list.visibleCount', { count: activeAndUpcoming().length + past().length }) }}</p>
        }
      </header>

      @if (loading()) {
        <p class="state">{{ t('public.list.loading') }}</p>
      } @else if (error()) {
        <p class="state state--error">{{ error() }}</p>
      } @else if (activeAndUpcoming().length === 0 && past().length === 0) {
        <p class="state">{{ t('public.list.empty') }}</p>
      } @else {
        <section class="block">
          <div class="block-header">
            <h2>{{ t('public.list.activeBlock') }}</h2>
            <p>{{ t('public.list.activeHint') }}</p>
            <span class="count">{{ activeAndUpcoming().length }}</span>
          </div>
          @if (activeAndUpcoming().length === 0) {
            <p class="state">{{ t('public.list.activeEmpty') }}</p>
          } @else {
            <ul class="grid">
              @for (tournament of activeAndUpcoming(); track tournament.slug) {
                <li class="card">
                  <a [routerLink]="['/tournaments', tournament.slug]">
                    <div class="title-row">
                      <h3>{{ tournament.name }}</h3>
                      @if (isInProgress(tournament)) {
                        <span class="live-badge">{{ t('public.list.inProgress') }}</span>
                      }
                    </div>
                    <p class="subtitle">{{ tournament.game }} - {{ tournament.format_label }}</p>
                    <div class="meta-row">
                      <span class="pill">{{ t('public.list.region', { value: tournament.region }) }}</span>
                      <span class="pill">{{ t('public.list.start', { value: (tournament.starts_at | date: 'short') ?? '' }) }}</span>
                    </div>
                    <p class="action">{{ t('public.list.viewDetail') }}</p>
                  </a>
                </li>
              }
            </ul>
          }
        </section>

        <section class="block">
          <div class="block-header">
            <h2>{{ t('public.list.pastBlock') }}</h2>
            <p>{{ t('public.list.pastHint') }}</p>
            <span class="count count--muted">{{ past().length }}</span>
          </div>
          @if (past().length === 0) {
            <p class="state">{{ t('public.list.pastEmpty') }}</p>
          } @else {
            <ul class="grid">
              @for (tournament of past(); track tournament.slug) {
                <li class="card card--past">
                  <a [routerLink]="['/tournaments', tournament.slug]">
                    <h3>{{ tournament.name }}</h3>
                    <p class="subtitle">{{ tournament.game }} - {{ tournament.format_label }}</p>
                    <div class="meta-row">
                      <span class="pill">{{ t('public.list.region', { value: tournament.region }) }}</span>
                      <span class="pill">{{ t('public.list.start', { value: (tournament.starts_at | date: 'short') ?? '' }) }}</span>
                    </div>
                    <p class="action">{{ t('public.list.viewDetail') }}</p>
                  </a>
                </li>
              }
            </ul>
          }
        </section>
      }
    </section>
  `,
  styles: `
    .page { padding: 1.2rem; max-width: 1100px; margin: 0 auto; }
    .block { margin-bottom: 1rem; border: 1px solid var(--line-soft); border-radius: 14px; background: linear-gradient(180deg, rgba(20, 31, 51, 0.9) 0%, rgba(15, 24, 40, 0.9) 100%); padding: 0.9rem; box-shadow: var(--shadow-soft); }
    .block-header { margin: 0 0 0.55rem; display: grid; grid-template-columns: 1fr auto; gap: 0.2rem 0.6rem; align-items: center; }
    .block-header h2 { margin: 0; font-size: 1.12rem; color: var(--text-strong); }
    .block-header p { margin: 0.1rem 0 0; color: var(--text-muted); font-size: 0.9rem; grid-column: 1 / 2; }
    .count { grid-column: 2 / 3; grid-row: 1 / 3; align-self: center; font-size: 0.78rem; font-weight: 700; color: #d8e8ff; background: rgba(106, 162, 255, 0.2); border: 1px solid rgba(106, 162, 255, 0.46); border-radius: 999px; padding: 0.18rem 0.5rem; }
    .count--muted { color: #cfdaed; background: rgba(141, 156, 183, 0.2); border-color: rgba(141, 156, 183, 0.4); }
    .card { border: 1px solid var(--line-soft); border-radius: 12px; background: linear-gradient(180deg, rgba(23, 36, 58, 0.96) 0%, rgba(18, 29, 47, 0.96) 100%); box-shadow: var(--shadow-soft); }
    .card--past { opacity: 0.96; }
    .page-header { padding: 1rem; margin-bottom: 0.95rem; border: 1px solid var(--line-soft); border-radius: 14px; background: linear-gradient(180deg, rgba(26, 40, 64, 0.92) 0%, rgba(18, 29, 47, 0.92) 100%); box-shadow: var(--shadow-soft); }
    .page-header h1 { margin: 0 0 0.35rem; font-size: 1.55rem; color: var(--text-strong); }
    .page-header p { margin: 0; color: var(--text-muted); }
    .kpi { margin-top: 0.55rem !important; color: #d8e8ff !important; font-weight: 600; }
    .grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(260px, 1fr)); gap: 0.8rem; list-style: none; padding: 0; margin: 0; }
    .card a { display: block; color: inherit; text-decoration: none; padding: 0.95rem; min-height: 145px; transition: transform 120ms ease, box-shadow 120ms ease; }
    .card a:hover { transform: translateY(-1px); box-shadow: 0 10px 22px rgba(0, 0, 0, 0.35); }
    .title-row { display: flex; justify-content: space-between; align-items: start; gap: 0.5rem; margin-bottom: 0.35rem; }
    .card h3 { margin: 0; font-size: 1.08rem; color: var(--text-strong); }
    .subtitle { margin: 0; font-size: 0.92rem; color: var(--text-muted); }
    .meta-row { display: flex; flex-wrap: wrap; gap: 0.35rem; margin-top: 0.6rem; }
    .pill { font-size: 0.8rem; color: #d4e2fb; background: rgba(84, 118, 173, 0.3); border: 1px solid rgba(104, 146, 214, 0.45); border-radius: 999px; padding: 0.2rem 0.5rem; }
    .live-badge { font-size: 0.75rem; font-weight: 700; color: var(--ok-text); background: rgba(17, 47, 33, 0.8); border: 1px solid var(--ok-line); border-radius: 999px; padding: 0.18rem 0.48rem; white-space: nowrap; }
    .action { margin: 0.75rem 0 0; color: var(--accent); font-weight: 600; font-size: 0.9rem; }
    .state { color: var(--text-muted); margin: 0.35rem 0; }
    .state--error { color: var(--danger); }
  `
})
export class TournamentsListPageComponent {
  private readonly api = inject(TournamentsApiService);
  private readonly i18n = inject(I18nService);
  private readonly destroyRef = inject(DestroyRef);

  protected readonly activeAndUpcoming = signal<TournamentPublic[]>([]);
  protected readonly past = signal<TournamentPublic[]>([]);
  protected readonly loading = signal(true);
  protected readonly error = signal<string | null>(null);

  protected t(key: string, params?: Record<string, string | number | null | undefined>): string {
    return this.i18n.t(key, params);
  }

  constructor() {
    this.api
      .listPublicTournaments()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response) => {
          const grouped = this.groupTournaments(response.tournaments ?? []);
          this.activeAndUpcoming.set(grouped.activeAndUpcoming);
          this.past.set(grouped.past);
          this.loading.set(false);
        },
        error: () => {
          this.error.set(this.i18n.t('public.list.error'));
          this.loading.set(false);
        }
      });
  }

  protected isInProgress(tournament: TournamentPublic): boolean {
    return tournament.lifecycle_status === 'ongoing';
  }

  private groupTournaments(all: TournamentPublic[]): { activeAndUpcoming: TournamentPublic[]; past: TournamentPublic[] } {
    const sorted = [...all].sort((a, b) => {
      const dateA = new Date(a.starts_at).getTime();
      const dateB = new Date(b.starts_at).getTime();
      return dateA - dateB;
    });

    const activeAndUpcoming = sorted.filter((tournament) => tournament.lifecycle_status !== 'past');
    const past = sorted.filter((tournament) => tournament.lifecycle_status === 'past').reverse();

    return { activeAndUpcoming, past };
  }
}
